import moment from "moment";
import sessionHandling from "services/utils/notificationUtils";
import { qtyNumber } from "./shiftEarningsData";

const BASE = "payroll/earning/shift/";
export const apiErrorText = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(apiErrorText).join(" ");
  if (value && typeof value === "object") return Object.entries(value).map(([key, item]) => `${key}: ${apiErrorText(item)}`).join(" ");
  return "";
};

export const shiftRequest = async (domain, path, { method = "GET", body, signal } = {}) => {
  // FormData (file uploads) goes as-is so the browser sets the multipart header.
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const response = await fetch(`${domain.replace(/\/$/, "")}/${path}`, {
    method, signal,
    headers: { ...(isForm ? {} : { "Content-Type": "application/json" }), Authorization: `Token ${localStorage.getItem("token")}` },
    ...(body === undefined ? {} : { body: isForm ? body : JSON.stringify(body) }),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new Error("Your session has expired. Please sign in again.");
  }
  if (response.status === 409) sessionHandling();
  if (response.status === 204 && response.ok) return null;
  const data = await response.json().catch(() => null);
  // Bulk actions answer 400 {"errors": [{"id", "message"}]} when no row could
  // be updated: shown as "Record 103: <message>".
  const rowErrors = Array.isArray(data?.errors)
    ? data.errors.map((item) => (item?.id != null ? `Record ${item.id}: ${apiErrorText(item.message)}` : apiErrorText(item))).join(" ")
    : "";
  if (!response.ok) throw new Error(apiErrorText(data?.error || data?.detail) || rowErrors || apiErrorText(data) || `Request failed (${response.status}). Please try again.`);
  return data;
};

const finiteOrNull = (value) => {
  const number = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(number) ? null : number;
};

// Rate shown for an entry (list Rate column, view "Payable Qty × Rate", Edit
// form). The API row only carries per_hour_rate, but a shift-basis amount is
// priced per shift (per-day rate): showing the hourly figure gave e.g.
// "1.00 × ₹54.17 = ₹650.00". Shift basis therefore uses a per-day rate field
// when the row has one, else amount ÷ payable qty (the rate the server
// priced the entry at); hours basis keeps per_hour_rate.
export const shiftRowRate = (row) => {
  if (row.basis !== "shift") return finiteOrNull(row.per_hour_rate);
  const perDay = [row.per_day_rate, row.per_day_salary, row.per_day].map(finiteOrNull).find((value) => value != null);
  if (perDay != null) return perDay;
  const amount = finiteOrNull(row.earning_amount);
  const qty = qtyNumber(row.adjusted) ?? (finiteOrNull(row.quantity) != null ? finiteOrNull(row.quantity) * (finiteOrNull(row.payment_factor) ?? 1) : null);
  if (amount != null && qty) return Math.round((amount / qty) * 100) / 100;
  return finiteOrNull(row.per_hour_rate);
};

export const fromShiftApi = (row) => {
  const date = moment(row.date, ["DD-MM-YYYY", "YYYY-MM-DD"], true);
  return {
    ...row,
    date: date.isValid() ? date.format("YYYY-MM-DD") : row.date,
    worked_shift_hours: row.worked_shift_hours ?? row.worked_shift_duration ?? null,
    rate: shiftRowRate(row),
    payable_amount: row.net_pay ?? row.earning_amount,
    payroll_cycle: row.pay_schedule_name || "Unassigned",
    pay_schedule_id: row.pay_schedule,
  };
};

export const toShiftPayload = (form, { creating = false } = {}) => ({
  ...(creating ? { employee_id: form.employee_code } : {}),
  date: form.date,
  earning_type: form.earning_type,
  ...(creating && !form.shift_name ? {} : { shift: form.shift_name || null }),
  ...(creating && !String(form.worked_shift_hours ?? "").trim() ? {} : { worked_shift_hours: String(form.worked_shift_hours ?? "").trim() || null }),
  basis: form.basis,
  quantity: Number(form.quantity),
  rate: Number(form.rate),
  factor: Number(form.payment_factor),
  ...(form.pay_schedule_id === "" ? (creating ? {} : { pay_schedule_id: null }) : { pay_schedule_id: Number(form.pay_schedule_id) }),
  ...(!creating ? { status: form.status } : {}),
});

export const listShiftEarnings = async (domain, companyId, period, signal) => {
  const params = new URLSearchParams({ company_id: companyId, start_date: period.start, end_date: period.end });
  const data = await shiftRequest(domain, `${BASE}earning_details/?${params}`, { signal });
  if (!Array.isArray(data)) throw new Error("The server returned an invalid shift earnings list.");
  return data.filter((row) => !row.isdeleted).map(fromShiftApi);
};
export const saveShiftEarning = async (domain, form, id, signal, initial) => {
  const payload = toShiftPayload(form, { creating: id == null });
  const before = initial ? toShiftPayload(initial) : null;
  const body = before ? Object.fromEntries(Object.entries(payload).filter(([key, value]) => value !== before[key])) : payload;
  const row = await shiftRequest(domain, `${BASE}earning/${id == null ? "" : `${id}/`}`, {
    method: id == null ? "POST" : "PUT", body, signal,
  });
  if (row?.id == null) throw new Error("The server returned an invalid shift earning.");
  return fromShiftApi(row);
};
export const deleteShiftEarning = (domain, id, signal) => shiftRequest(domain, `${BASE}earning/${id}/`, { method: "DELETE", signal });
export const bulkShiftEarnings = async (domain, ids, changes, signal) => {
  const data = await shiftRequest(domain, `${BASE}earning/bulk-action/`, { method: "PUT", body: { ids, ...changes }, signal });
  if (!Array.isArray(data?.updated) || !Array.isArray(data?.errors)) throw new Error("The server returned an invalid bulk action result. Refresh the list before trying again.");
  return data;
};
// Import: one payroll cycle for the whole file. The server checks each record
// (employee, shift, ...) and answers { total, success, failed, rows: [{
// employee_id, employee_name, date, status: "success" | "failed", message }] }.
export const importShiftEarnings = async (domain, payScheduleId, records, signal) => {
  const data = await shiftRequest(domain, `${BASE}import/`, {
    // No payroll cycle chosen (Automatic): pay_schedule_id is left out and the
    // server picks each row's cycle by employee + date, as validate does.
    method: "POST", body: { ...(payScheduleId ? { pay_schedule_id: Number(payScheduleId) } : {}), records }, signal,
  });
  if (!Array.isArray(data?.rows)) throw new Error("The server returned an invalid import result. Refresh the list before trying again.");
  return data;
};

// Checks the records like the import does, without saving. Without a payroll
// cycle the server picks each row's cycle by employee + date. Answers
// { total, valid, failed, rows: [{ index, status: "valid" | "failed",
// message, amount, pay_schedule_name }] }, index = position in `records`.
export const validateShiftImport = async (domain, payScheduleId, records, signal) => {
  const data = await shiftRequest(domain, `${BASE}import/validate/`, {
    method: "POST",
    body: { ...(payScheduleId ? { pay_schedule_id: Number(payScheduleId) } : {}), records },
    signal,
  });
  if (!Array.isArray(data?.rows)) throw new Error("The server returned an invalid validation result.");
  return data;
};

// The company's payroll cycles (same list Penalties and Incentives use).
export const loadCompanyPaySchedules = async (domain, companyId, signal) => {
  const data = await shiftRequest(domain,
    `payroll/operations/payrollprocessing/payrollruncycle/list-payschedules/?${new URLSearchParams({ company_id: companyId })}`, { signal });
  const list = Array.isArray(data) ? data : data?.results;
  if (!Array.isArray(list)) throw new Error("The server returned an invalid payroll cycle list.");
  return list.filter((item) => item?.id != null).map((item) => ({ value: String(item.id), label: item.name || String(item.id), start_date: item.start_date, end_date: item.end_date }));
};

// ---- From the System (Daily Payroll Reprocess, same API as main's
// Shift Earnings "Reprocess" button) ----
const listOf = (data, what) => {
  const list = Array.isArray(data) ? data : data?.results;
  if (!Array.isArray(list)) throw new Error(`The server returned an invalid ${what} list.`);
  return list;
};

export const loadReprocessDepartments = async (domain, userId, companyId, signal) => {
  const data = await shiftRequest(domain, `get-department/?${new URLSearchParams({ user_id: userId, company_id: companyId })}`, { signal });
  return listOf(data, "department")
    .filter((item) => item?.id != null)
    .map((item) => ({ value: String(item.id), label: item.department_name || String(item.id) }));
};

// departmentId: "" = all departments. last_pay_date (DD-MM-YYYY) is the
// earliest date that employee can be reprocessed from.
export const loadReprocessEmployees = async (domain, companyId, departmentId, signal) => {
  const data = await shiftRequest(domain,
    `list/employee?${new URLSearchParams({ company_id: companyId, department_id: departmentId || 99999, status: "Active" })}`, { signal });
  return listOf(data, "employee")
    .filter((item) => item?.user_id != null)
    .map((item) => {
      const lastPay = moment(item.last_pay_date, ["DD-MM-YYYY", "YYYY-MM-DD"], true);
      return {
        value: String(item.user_id),
        id: String(item.user_id),
        user_id: item.user_id,
        employee_id: item.employee_id ? String(item.employee_id) : "",
        label: item.user_name || item.first_name || item.employee_name || String(item.user_id),
        image: item.image_url || "",
        image_url: item.image_url || "",
        last_pay_date: lastPay.isValid() ? lastPay.format("YYYY-MM-DD") : "",
      };
    });
};

// userId 99999 = all employees (main's default when none is picked).
export const reprocessDailySalary = (domain, { startDate, endDate, userId }, signal) =>
  shiftRequest(domain, "payroll/reprocess_daily_salary/", {
    method: "POST",
    body: { start_date: startDate, end_date: endDate, user_id: userId ?? 99999 },
    signal,
  });

// Attendance records still waiting to reach payroll in the period (same call
// and count rule as main's Shift Earnings screen). Above 0 = the list may be
// incomplete.
export const loadReadyForPayrollCount = async (domain, period, signal) => {
  const data = await shiftRequest(domain,
    `payroll/earning/ready-for-payroll/counts/?${new URLSearchParams({ start_date: period.start, end_date: period.end })}`, { signal });
  if (typeof data === "number") return data;
  return (Number(data?.attendance_ready_for_payroll_count) || 0)
    + (Number(data?.holiday_ready_for_payroll_count) || 0)
    + (Number(data?.post_ot_ready_for_payroll_count) || 0)
    + (Number(data?.week_off_ready_for_payroll_count) || 0);
};

// Day Type options for the Add/Edit form: a plain list of names, e.g.
// ["Week Day", "Weekoff Attendance", "Holiday", ...].
export const loadShiftDayTypes = async (domain, signal) => {
  const data = await shiftRequest(domain, `${BASE}earning/types/`, { signal });
  const list = Array.isArray(data) ? data : data?.results;
  if (!Array.isArray(list)) throw new Error("The server returned an invalid day type list.");
  return list.map((item) => (typeof item === "string" ? item : item?.name ?? item?.label ?? "")).filter(Boolean);
};

export const loadShiftActivity =(domain, id, signal) => shiftRequest(domain, `${BASE}earning_details/${id}/activity/`, { signal });

export const loadShiftFormOptions = async (domain, companyId, signal) => {
  const results = await Promise.allSettled([
    shiftRequest(domain, `list/employee?${new URLSearchParams({ company_id: companyId, department_id: 99999, status: "Active" })}`, { signal }),
    shiftRequest(domain, "administration/shiftroster/shift/shifts", { signal }),
  ]);
  const names = ["employees", "shifts"];
  const options = { employees: [], shifts: [], errors: [] };
  results.forEach((result, index) => {
    // Lists may come back plain or paginated ({ results: [...] }), same as
    // useFetch accepts; only a failed request or another shape is an error.
    const list = result.status === "fulfilled"
      ? (Array.isArray(result.value) ? result.value : result.value?.results)
      : null;
    if (!Array.isArray(list)) {
      options.errors.push(`Could not load ${names[index]}. ${result.reason?.message || "The server returned an unexpected response."}`);
      return;
    }
    options[names[index]] = index === 0
      // Shaped for CustomAutoCompleteWithLogo (photo, name, employee code),
      // the picker Team Workspace Approvals uses.
      ? list.filter((employee) => employee.employee_id).map((employee) => ({
          value: String(employee.employee_id),
          id: String(employee.employee_id),
          employee_id: String(employee.employee_id),
          // Numeric user id (distinct from the employee code above) — the
          // per-day/per-hour salary lookup is keyed by this, not the code.
          user_id: employee.user_id != null ? String(employee.user_id) : "",
          label: employee.user_name || employee.username || employee.employee_name || String(employee.employee_id),
          image: employee.image_url || "",
          // Shown on the picked employee's card.
          image_url: employee.image_url || "",
          department_name: employee.department_name || employee.department || "",
          designation: employee.designation_name || employee.designation || "",
        }))
      // duration ("HH:MM") is the shift's configured working hours, used to
      // auto-fill Working Hours once a shift is picked.
      : list.filter((shift) => shift?.name).map((shift) => ({ value: shift.name, label: shift.name, duration: shift.duration }));
  });
  return options;
};

export const loadShiftSchedules = async (domain, employeeCode, signal) => {
  if (!employeeCode) return [];
  const data = await shiftRequest(domain,
    `${BASE}earning/pay-schedules/?${new URLSearchParams({ employee_id: employeeCode })}`, { signal });
  const list = Array.isArray(data) ? data : data?.results;
  if (!Array.isArray(list)) throw new Error("The server returned an invalid payroll schedule list.");
  return [...new Map(list.map((item) => [item.id, { ...item, value: String(item.id), label: item.name }])).values()];
};

export const loadCommonShiftSchedules = async (domain, employeeCodes, signal) => {
  const codes = [...new Set(employeeCodes.filter(Boolean))];
  if (!codes.length) return [];
  const lists = await Promise.all(codes.map((code) => loadShiftSchedules(domain, code, signal)));
  return lists[0].filter((option) => lists.every((list) => list.some((item) => item.value === option.value)));
};

// The employee's per-day/per-hour pay rate, used to auto-fill the Rate field
// when adding a shift earning. Without a payroll cycle selected, the server
// has no cycle to price against, so pay_schedule_id is sent as the "none"
// sentinel it expects (99999) and the entry's date is sent instead.
export const loadUserPerDayPerHourSalary = async (domain, { userId, payScheduleId, date, signal }) => {
  if (!userId) return null;
  const params = new URLSearchParams({
    pay_schedule_id: payScheduleId || "99999",
    user_id: userId,
    ...(payScheduleId ? {} : { date }),
  });
  return shiftRequest(domain, `payroll/per-day-per-hour-salary/user/?${params}`, { signal });
};

// Company-wide per-day/per-hour pay rate list for a payroll cycle — the same
// data as Pay Processing > Daily Payrun > "Daily / Hourly Pay Rate" report
// (DailyPayroll/index.js). Used to show one employee's row (double-clicking
// a Shift Earning) with that report's exact columns: user_id is the
// employee CODE here (not the numeric id the /user/ endpoint above takes).
export const loadPerDayPerHourSalaryList = async (domain, { payScheduleId, companyId, signal }) => {
  if (!payScheduleId) return [];
  const params = new URLSearchParams({ pay_schedule_id: payScheduleId, company_id: companyId });
  const data = await shiftRequest(domain, `payroll/per-day-per-hour-salary/?${params}`, { signal });
  return Array.isArray(data) ? data : [];
};
